// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROGRAMMES
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

import { consumeLastModifiedAnimation, markLastModified } from '../utils/last-modified-anim.js';
import { openPilotPicker } from '../utils/pilot-picker.js';
import {
  buildProgramGeneralFields,
  buildProgramProjCheckboxes,
  readProgramPayload,
  wireProgramFormInputs,
} from './page-programs-fragments.js';
import {
  buildCommitteePilotRows,
  buildModalTabs,
  buildPilotChips,
  buildPilotsCell,
  buildPilotsTab,
  createPilotChipsRefresher,
  indexCommitteePilots,
  loadPilotRefs,
  pilotsSortKey,
  wireCommitteePilotDots,
  wireModalTabs,
  type CommitteePilot,
  type CommitteeRow,
  type UserMin,
} from './pilots-form.js';

export interface ProjectMin {
  id: number;
  code?: string | null;
  name: string;
}

interface Program {
  id: number;
  code: string;
  name: string;
  date_start?: string | null;
  date_end?: string | null;
  projects: ProjectMin[];
  pilots?: number[];
  committee_pilots?: CommitteePilot[];
}

declare global {
  interface Window {
    renderProgramsPage: typeof renderProgramsPage;
    showProgramModal: typeof showProgramModal;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROGRAMME (édition) — RefactoUX mai 2026
// Remplace l'ancienne popup `showProgramModal` pour l'édition. Layout 1/3 + 2/3
// (col gauche : Général + Pilote du programme ; col droite : Pilotes des
// comités sur 2 sous-colonnes). La création ad-hoc d'un programme garde la
// modale pour rester rapide depuis la liste.
// ═══════════════════════════════════════════════════════════════════════════
import { definePage } from '../router.js';

pages.program = definePage<'program/:id'>(async ({ id }) => {
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    // Pas d'endpoint GET /programs/{id} : on récupère la liste complète et on
    // filtre. Coût acceptable pour la page édition (qq dizaines de programmes
    // typiques par tenant).
    const [programs, projects, users, committees] = (await Promise.all([
      api.getPrograms(),
      api.getProjects(),
      api.getUsers(),
      api.getCommittees(),
    ])) as [Program[], ProjectMin[], UserMin[], CommitteeRow[]];
    const program = programs.find((p) => p.id === id);
    if (!program) {
      setContent(
        `<div class="error" style="padding:20px">Programme #${esc(String(id))} introuvable.</div>`,
      );
      return;
    }
    renderProgramEditPage(program, programs, projects, users, committees);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

function renderProgramEditPage(
  program: Program,
  _programs: ReadonlyArray<Program>,
  projects: ReadonlyArray<ProjectMin>,
  users: UserMin[],
  committees: CommitteeRow[],
): void {
  const pid = program.id;
  const indivUsers = (users || []).filter((u) => !u.is_team);
  const selectedPilots = new Set<number>(program.pilots || []);
  const currentPids = new Set<number>(program.projects.map((x) => x.id));

  // Puces pilotes + cases projets : fragments partagés (page-programs-fragments,
  // pilots-form). Fallback « aucun pilote » propre à la page d'édition (picto ⚠).
  const renderChips = (): string => {
    const chips = buildPilotChips(selectedPilots, indivUsers, 'pgm');
    if (chips) return chips;
    return `<span style="color:var(--warn);font-size:14px;cursor:help" title="${esc(t('pagePrograms.btnAddPilotTitle'))}">⚠</span>
      <span style="color:var(--text3);font-size:11px;margin-left:6px">${esc(t('pagePrograms.noPilotDesignated'))}</span>`;
  };

  const renderProjCheckboxes = (filter: string): string =>
    buildProgramProjCheckboxes(projects, filter, (id) => currentPids.has(id));

  // Lignes comités empilées (pastille / « CODE Nom » / sélecteur) : la
  // colonne est étroite, contrairement à la grille sur une ligne de la modale.
  const committeesHtml = buildCommitteePilotRows(
    committees,
    indexCommitteePilots(program.committee_pilots),
    indivUsers,
    {
      prefix: 'pgm',
      missingLabel: t('pagePrograms.pilotMissing'),
      emptyLabel: t('pagePrograms.noCommittee'),
      row: (c, parts) =>
        `<div class="pgm-cp-row" data-cid="${c.id}" style="display:grid;grid-template-columns:14px 1fr;gap:6px;align-items:center;padding:4px 0;border-bottom:1px solid var(--border)">
          ${parts.dotHtml}
          <div>
            <div style="font-size:12px;color:var(--text2);margin-bottom:2px">${parts.codeNameHtml}</div>
            ${parts.selectHtml}
          </div>
        </div>`,
    },
  );

  // ── Header : breadcrumb + boutons Annuler/Enregistrer ──
  setHeader(
    `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('vocabulary.program_plural'))}</a> / ${esc(program.name)}`,
    `<button class="btn btn-neutral ml-auto" id="pgm-btn-cancel" style="margin-right:6px">${esc(t('common.cancel'))}</button>` +
      `<button class="btn btn-primary" id="pgm-btn-save">${esc(t('common.save'))}</button>`,
  );

  setContent(`
    <!-- Layout 3 colonnes égales (mai 2026) :
         col 1 : Général + Pilote du programme empilés
         col 2 : Pilotes des comités (1 seule sous-col, items pleine largeur)
         col 3 : Projets associés (sorti du bloc Général) -->
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;align-items:start">
      <!-- Col 1 : Général + Pilote du programme -->
      <!-- Padding et titre alignés sur les cards des col 2/3 pour que les
           3 blocs aient le même haut visuel (sans align-items, les paddings
           différents créaient un décalage de 2-4px). -->
      <div style="display:flex;flex-direction:column;gap:10px">
        <!-- Bloc Général (sans la liste de projets) -->
        <div class="card" style="padding:12px 14px">
          <h3 style="margin:0 0 10px 0;font-size:13px">${esc(t('pagePrograms.tabGeneral'))}</h3>
          ${buildProgramGeneralFields(program)}
        </div>

        <!-- Bloc Pilote du programme -->
        <div class="card" style="padding:12px 14px">
          <h3 style="margin:0 0 6px 0;font-size:13px">${esc(t('pagePrograms.tabPilots', { object: t('vocabulary.program') }))}</h3>
          <div id="pgm-pilots-block"
            title="${esc(t('pagePrograms.btnAddPilotTitle'))}"
            style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;display:flex;align-items:flex-start;gap:6px;cursor:pointer">
            <div id="pgm-pilots-chips" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px;align-items:center">${renderChips()}</div>
            <button type="button" id="pgm-pilot-add-btn" class="btn btn-secondary btn-icon"
              style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(t('pagePrograms.btnAddPilotTitle'))}">+</button>
          </div>
        </div>
      </div>

      <!-- Col 2 : Pilotes des comités (1 sous-col, items pleine largeur du bloc) -->
      <div class="card" style="padding:12px 14px">
        <h3 style="margin:0 0 10px 0;font-size:13px">${esc(t('pagePrograms.tabCommitteePilots', { plural: t('vocabulary.committee_plural') }))}</h3>
        <div id="pgm-committees">${committeesHtml}</div>
      </div>

      <!-- Col 3 : Projets associés (sortis de Général) -->
      <div class="card" style="padding:12px 14px">
        <h3 style="margin:0 0 10px 0;font-size:13px">${esc(t('pagePrograms.lblLinkedProjects'))}</h3>
        <input type="text" id="pm-proj-search" placeholder="${esc(t('pagePrograms.phSearchProject'))}" autocomplete="off"
          style="width:100%;font-size:12px;padding:5px 8px;margin:0 0 8px;border:1px solid var(--border);border-radius:var(--radius)">
        <div id="pm-proj-list" style="max-height:520px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius);padding:8px 10px">
          ${renderProjCheckboxes('')}
        </div>
      </div>
    </div>
  `);

  // ── Wiring ──
  // Garde "modifications non enregistrées" (audit UX 2026-06).
  let _progEditDirty = false;
  const _markProgEditDirty = (): void => {
    _progEditDirty = true;
  };
  const _progEditContent = document.getElementById('content');
  _progEditContent?.addEventListener('input', _markProgEditDirty);
  _progEditContent?.addEventListener('change', _markProgEditDirty);
  setUnsavedGuard(() => _progEditDirty);

  wireProgramFormInputs(renderProjCheckboxes);

  const refreshChips = createPilotChipsRefresher(
    'pgm',
    renderChips,
    selectedPilots,
    _markProgEditDirty,
  );
  refreshChips();

  const openPicker = (anchor: HTMLElement): void =>
    openPilotPicker({
      anchor,
      users: indivUsers,
      selected: selectedPilots,
      prefix: 'pgm-pilot',
      labels: {
        search: t('pagePrograms.phPilotSearch'),
        empty: t('pagePrograms.noResult'),
        add: t('pagePrograms.btnAdd'),
      },
      onChange: _markProgEditDirty,
      onClose: refreshChips,
    });

  // Clic sur le bouton "+" OU sur n'importe quelle zone du bloc pilotes
  // (hors chip déjà sélectionnée) → ouvre le picker.
  document.getElementById('pgm-pilot-add-btn')?.addEventListener('click', (ev: Event) => {
    ev.stopPropagation();
    openPicker(ev.currentTarget as HTMLElement);
  });
  document.getElementById('pgm-pilots-block')?.addEventListener('click', (ev: Event) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.pgm-pilot-chip') || target.closest('.pgm-pilot-remove')) return;
    if (target.closest('#pgm-pilot-add-btn')) return;
    const btn = document.getElementById('pgm-pilot-add-btn') as HTMLElement | null;
    if (btn) openPicker(btn);
  });

  wireCommitteePilotDots('pgm', t('pagePrograms.pilotMissing'));

  // Save / Annuler.
  const doSave = async (): Promise<void> => {
    const payload = readProgramPayload(selectedPilots);
    if (!payload) return;
    try {
      await api.updateProgram(pid, payload);
      toast(t('pagePrograms.toastUpdated'));
      markLastModified('programs', pid);
      clearUnsavedGuard();
      window.location.hash = '#programs';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast(msg, 'error');
    }
  };
  document.getElementById('pgm-btn-save')?.addEventListener('click', (e: Event) => {
    const btn = e.currentTarget as HTMLButtonElement;
    void api.withButton(btn, doSave);
  });
  document.getElementById('pgm-btn-cancel')?.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmLeaveIfDirty())) return;
      window.location.hash = '#programs';
    })();
  });
}

pages.programs = async function () {
  setHeader(
    t('vocabulary.program_plural'),
    `<button class="btn btn-primary ml-auto" id="btn-new-program">${esc(t('programs.btn.add'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [programs, projects, users] = (await Promise.all([
      api.getPrograms(),
      api.getProjects(),
      api.getUsers(),
    ])) as [Program[], ProjectMin[], UserMin[]];
    renderProgramsPage(programs, projects, users);
    // Animation post-MAJ (cf. utils/last-modified-anim.ts).
    setTimeout(
      () =>
        consumeLastModifiedAnimation('programs', (id) =>
          document.querySelector<HTMLTableRowElement>(`#prog-table tr[data-program-id="${id}"]`),
        ),
      50,
    );
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderProgramsPage(
  programs: Program[],
  projects: ReadonlyArray<ProjectMin>,
  users: ReadonlyArray<UserMin>,
): void {
  let sortKey: 'code' | 'name' | 'date_start' | 'date_end' | 'projects' | 'pilots' = 'code';
  let sortDir: 'asc' | 'desc' = 'asc';
  const userMap: Record<number, UserMin> = Object.fromEntries((users || []).map((u) => [u.id, u]));

  const sort = (arr: ReadonlyArray<Program>): Program[] =>
    [...arr].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortKey) {
        case 'code':
          va = a.code.toLowerCase();
          vb = b.code.toLowerCase();
          break;
        case 'name':
          va = a.name.toLowerCase();
          vb = b.name.toLowerCase();
          break;
        case 'date_start':
          va = a.date_start || '';
          vb = b.date_start || '';
          break;
        case 'date_end':
          va = a.date_end || '';
          vb = b.date_end || '';
          break;
        case 'projects':
          va = a.projects.length;
          vb = b.projects.length;
          break;
        case 'pilots':
          va = pilotsSortKey(a.pilots, userMap);
          vb = pilotsSortKey(b.pilots, userMap);
          break;
        default:
          va = a.code;
          vb = b.code;
      }
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    });

  const th = (key: typeof sortKey, label: string): string => {
    const active = sortKey === key;
    return `<th data-sort-prog="${key}" style="cursor:pointer;user-select:none">${label} <span class="sort-arrow" style="font-size:10px">${active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  const buildRows = (): string =>
    sort(programs)
      .map(
        (p) => `<tr data-program-id="${p.id}">
    <td style="cursor:pointer;font-family:var(--font-mono);font-size:12px;font-weight:600;text-decoration:underline" data-click-prog="${p.id}">${esc(p.code)}</td>
    <td style="cursor:pointer" data-click-prog="${p.id}">${esc(p.name)}</td>
    <td style="font-size:12px">${
      p.projects.length
        ? p.projects
            .map((pr) => {
              const tip = `${esc(pr.code || '')}${pr.code && pr.name ? ', ' : ''}${esc(pr.name || '')}`;
              return `<span title="${tip}" style="background:var(--bg3);border-radius:4px;padding:1px 6px;margin-right:3px;font-size:11px;cursor:default">${esc(pr.code || pr.name)}</span>`;
            })
            .join('')
        : '<span style="color:var(--text3)">—</span>'
    }</td>
    <td>${buildPilotsCell(p.pilots, userMap, t('pagePrograms.warnDesignatePilots'))}</td>
    <td style="font-size:12px;color:var(--text3)">${p.date_start ? fmtDate(p.date_start) : '—'}</td>
    <td style="font-size:12px;color:var(--text3)">${p.date_end ? fmtDate(p.date_end) : '—'}</td>
    <td style="white-space:nowrap">
      ${
        p.projects.length
          ? `<a href="#matrix/program/${p.id}" style="font-size:12px;color:var(--accent);text-decoration:underline;margin-right:10px" title="${esc(t('pagePrograms.titleMatrix', { plural: t('vocabulary.risk_plural') }))}">${esc(t('pagePrograms.btnMatrix'))}</a>`
          : ''
      }
      <button class="btn btn-secondary btn-icon ml-1" data-edit-prog="${p.id}" title="${esc(t('common.edit'))}">✎</button>
      <button class="btn btn-danger btn-icon ml-1" data-del-prog="${p.id}" data-prog-name="${esc(p.name)}" data-proj-count="${p.projects.length}" title="${esc(t('common.delete'))}">✕</button>
    </td>
  </tr>`,
      )
      .join('');

  setContent(`<div class="card" id="prog-card">
    <div class="table-wrap"><table id="prog-table">
      <thead><tr>${th('code', esc(t('pagePrograms.colCode')))}${th('name', esc(t('pagePrograms.colName')))}${th('projects', esc(t('pagePrograms.colProjects')))}${th('pilots', esc(t('pagePrograms.colPilots')))}${th('date_start', esc(t('pagePrograms.colStart')))}${th('date_end', esc(t('pagePrograms.colEnd')))}<th></th></tr></thead>
      <tbody id="prog-tbody">${buildRows() || `<tr><td colspan="7" class="empty">${esc(t('pagePrograms.emptyTable'))}</td></tr>`}</tbody>
    </table></div>
  </div>`);

  document.getElementById('btn-new-program')?.addEventListener('click', () => {
    void showProgramModal(null, programs, projects, (created) => {
      programs.push(created);
      reRender();
    });
  });

  function reRender(): void {
    const tbody = document.getElementById('prog-tbody');
    if (tbody)
      tbody.innerHTML =
        buildRows() ||
        `<tr><td colspan="7" class="empty">${esc(t('pagePrograms.emptyTable'))}</td></tr>`;
    document.querySelectorAll<HTMLElement>('[data-sort-prog]').forEach((h) => {
      const a = h.dataset['sortProg'] === sortKey;
      const s = h.querySelector('.sort-arrow');
      if (s) s.textContent = a ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    });
  }

  document.getElementById('prog-card')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const sortTh = target.closest<HTMLElement>('[data-sort-prog]');
    if (sortTh) {
      const key = sortTh.dataset['sortProg'] as typeof sortKey;
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      reRender();
      return;
    }
    // RefactoUX mai 2026 : clic Code/Nom (data-click-prog) ou ✎ (data-edit-prog)
    // → navigation vers la page d'édition #program/:id (anciennement popup).
    // La création ad-hoc garde la popup (cf. btn-new-program ci-dessous).
    const clickCell = target.closest<HTMLElement>('[data-click-prog]');
    if (clickCell && !target.closest('button')) {
      const id = parseInt(clickCell.dataset['clickProg'] || '0');
      if (id > 0) window.location.hash = '#program/' + id;
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-prog]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editProg'] || '0');
      if (id > 0) window.location.hash = '#program/' + id;
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-prog]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delProg'] || '0');
      const name = delBtn.dataset['progName'] || '';
      const nProj = parseInt(delBtn.dataset['projCount'] || '0');
      const msg =
        nProj > 0
          ? `<p>${t(nProj > 1 ? 'pagePrograms.delConfirmManyProjects' : 'pagePrograms.delConfirmOneProject', { n: nProj })}</p><p style="font-size:12px;color:var(--text3)">${esc(t('pagePrograms.delConfirmHint'))}</p>`
          : `<p>${t('pagePrograms.delConfirmSimple', { name })}</p>`;
      openModal(
        t('pagePrograms.delModalTitle', { object: t('vocabulary.program') }),
        msg,
        async () => {
          try {
            await api.deleteProgram(id);
          } catch (e) {
            // 409 possible si le programme a des projets/risques liés.
            toast(e instanceof Error ? e.message : t('common.error'), 'error');
            return;
          }
          closeModal();
          toast(t('pagePrograms.toastDeleted'));
          programs.splice(
            programs.findIndex((x) => x.id === id),
            1,
          );
          reRender();
        },
        t('common.delete'),
      );
    }
  });
}

async function showProgramModal(
  program: Program | null,
  _programs: ReadonlyArray<Program>,
  projects: ReadonlyArray<ProjectMin>,
  onSaved: (saved: Program) => void,
): Promise<void> {
  const isNew = !program;
  const p: Program = program || {
    id: 0,
    code: '',
    name: '',
    date_start: '',
    date_end: '',
    projects: [],
    pilots: [],
    committee_pilots: [],
  };
  const currentPids = p.projects.map((x) => x.id);
  const { indivUsers, committees } = await loadPilotRefs();
  const selectedPilots = new Set<number>(p.pilots || []);
  // Puces pilotes : fragment partagé ; fallback « aucun pilote » propre à la modale.
  const renderChips = (): string =>
    buildPilotChips(selectedPilots, indivUsers, 'pgm') ||
    `<span style="color:var(--text3);font-size:11px">${esc(t('pagePrograms.noPilotDesignated'))}</span>`;

  const renderProjCheckboxes = (filter: string): string =>
    buildProgramProjCheckboxes(projects, filter, (id) => currentPids.includes(id));

  const tabGeneralHtml = `
    ${buildProgramGeneralFields(p)}
    <div class="form-group" style="margin-top:10px">
      <label style="font-weight:500">${esc(t('pagePrograms.lblLinkedProjects'))}</label>
      <input type="text" id="pm-proj-search" placeholder="${esc(t('pagePrograms.phSearchProject'))}" autocomplete="off"
        style="width:100%;font-size:12px;padding:5px 8px;margin:6px 0 4px;border:1px solid var(--border);border-radius:var(--radius)">
      <div id="pm-proj-list" style="max-height:180px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius);padding:8px 10px">
        ${renderProjCheckboxes('')}
      </div>
    </div>`;

  const body = buildModalTabs('pgm', {
    general: { label: t('pagePrograms.tabGeneral'), html: tabGeneralHtml },
    pilots: {
      label: t('pagePrograms.tabPilots', { object: t('vocabulary.program') }),
      html: buildPilotsTab('pgm', renderChips(), {
        helpHtml: t('pagePrograms.pilotsHelp'),
        addTitle: t('pagePrograms.btnAddPilotTitle'),
      }),
    },
    committees: {
      label: t('pagePrograms.tabCommitteePilots', { plural: t('vocabulary.committee_plural') }),
      html: buildCommitteePilotRows(
        committees,
        indexCommitteePilots(p.committee_pilots),
        indivUsers,
        {
          prefix: 'pgm',
          missingLabel: t('pagePrograms.pilotMissing'),
          emptyLabel: t('pagePrograms.noCommittee'),
        },
      ),
    },
  });

  openModal(
    isNew
      ? t('pagePrograms.modalTitleNew', { object: t('vocabulary.program') })
      : t('pagePrograms.modalTitleEdit', { object: t('vocabulary.program') }),
    body,
    async () => {
      const payload = readProgramPayload(selectedPilots);
      if (!payload) return;
      try {
        const result = (
          isNew ? await api.createProgram(payload) : await api.updateProgram(p.id, payload)
        ) as Program;
        closeModal();
        toast(isNew ? t('pagePrograms.toastCreated') : t('pagePrograms.toastUpdated'));
        onSaved(result);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('pagePrograms.btnAdd') : t('common.save'),
  );

  setTimeout(() => {
    wireProgramFormInputs(renderProjCheckboxes);
    wireModalTabs('pgm');

    const refreshChips = createPilotChipsRefresher('pgm', renderChips, selectedPilots);
    refreshChips();

    document.getElementById('pgm-pilot-add-btn')?.addEventListener('click', (ev: Event) => {
      ev.stopPropagation();
      openPilotPicker({
        anchor: ev.currentTarget as HTMLElement,
        users: indivUsers,
        selected: selectedPilots,
        prefix: 'pgm-pilot',
        labels: {
          search: t('pagePrograms.phSearchUser'),
          empty: t('pagePrograms.noResult'),
          add: t('pagePrograms.btnAdd'),
        },
        onClose: refreshChips,
      });
    });

    wireCommitteePilotDots('pgm', t('pagePrograms.pilotMissing'));
  }, 0);
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderProgramsPage = renderProgramsPage;
window.showProgramModal = showProgramModal;

export {};
